export const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] as const;
export const SCALE_DEGREES = [1, 2, 3, 4, 5, 6, 7] as const;

export type NoteName = (typeof NOTE_NAMES)[number];
export type ScaleDegree = (typeof SCALE_DEGREES)[number];
export type ScaleName = keyof typeof SCALES;
export type ChordQuality = keyof typeof CHORD_QUALITIES;

export type ResolvedChord = {
  key: NoteName;
  scale: ScaleName;
  degree: ScaleDegree;
  quality: ChordQuality;
  root: NoteName;
  rootOffset: number;
  name: string;
  notes: NoteName[];
  tones: ChordTone[];
  intervals: number[];
};

export type ChordTone = {
  note: NoteName;
  semitoneOffset: number;
};

export const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  pentatonic: [0, 2, 4, 7, 9, 12, 14],
} as const;

export const CHORD_QUALITIES = {
  base: { label: "Default", display: "Default" },
  flip: { label: "Flip", display: "Maj <-> Min" },
  dom7: { label: "Bluesy", display: "Dom7" },
  jazz: { label: "Jazzy", display: "Maj7 / min7" },
  add9: { label: "Lush", display: "9th" },
  sus4: { label: "Open", display: "Sus4" },
  sweet: { label: "Sweet", display: "6th / Sus2" },
  dark: { label: "Dark", display: "Dim / Min" },
  aug: { label: "Dreamy", display: "Aug" },
} as const;

export function resolveChord({
  key,
  scale,
  degree,
  quality,
}: {
  key: NoteName;
  scale: ScaleName;
  degree: ScaleDegree;
  quality: ChordQuality;
}): ResolvedChord {
  const keyIndex = NOTE_NAMES.indexOf(key);
  const scaleIntervals = SCALES[scale];
  const rootOffset = scaleIntervals[degree - 1] ?? 0;
  const rootIndex = keyIndex + rootOffset;
  const root = noteAt(rootIndex);
  const baseTones = scaleStepsToTones(keyIndex, scaleIntervals, degree - 1, [0, 2, 4]);
  const baseIntervals = baseTones.map(tone => tone.semitoneOffset - rootIndex);
  const intervals = applyModifier(quality, baseIntervals);
  const tones = intervals.map(interval => toneAt(rootIndex + interval));
  const notes = tones.map(tone => tone.note);

  return {
    key,
    scale,
    degree,
    quality,
    root,
    rootOffset: rootIndex,
    name: `${root}${symbolForIntervals(intervals)}`,
    notes,
    tones,
    intervals,
  };
}

export function noteAt(index: number): NoteName {
  return NOTE_NAMES[normalizeSemitone(index)]!;
}

function toneAt(semitoneOffset: number): ChordTone {
  return {
    note: noteAt(semitoneOffset),
    semitoneOffset,
  };
}

function scaleStepsToTones(keyIndex: number, scaleIntervals: readonly number[], rootStepIndex: number, steps: readonly number[]) {
  return steps.map(step => toneAt(keyIndex + scaleStepOffset(scaleIntervals, rootStepIndex + step)));
}

function scaleStepOffset(scaleIntervals: readonly number[], stepIndex: number) {
  const octave = Math.floor(stepIndex / scaleIntervals.length);
  const scaleIndex = ((stepIndex % scaleIntervals.length) + scaleIntervals.length) % scaleIntervals.length;
  return scaleIntervals[scaleIndex]! + octave * 12;
}

function applyModifier(quality: ChordQuality, baseIntervals: number[]) {
  const root = baseIntervals[0] ?? 0;
  const third = baseIntervals[1] ?? 4;
  const fifth = baseIntervals[2] ?? 7;
  const isMajor = third === 4 && fifth === 7;
  const isMinor = third === 3 && fifth === 7;

  switch (quality) {
    case "base":
      return baseIntervals;
    case "flip":
      return normalizeIntervals([root, third === 4 ? 3 : third === 3 ? 4 : third, fifth]);
    case "dom7":
      return [0, 4, 7, 10];
    case "jazz":
      return normalizeIntervals([...baseIntervals, isMajor ? 11 : 10]);
    case "add9":
      return normalizeIntervals([...baseIntervals, 14]);
    case "sus4":
      return [0, 5, 7];
    case "sweet":
      return isMajor ? [0, 4, 7, 9] : normalizeIntervals([0, 2, fifth]);
    case "dark":
      return isMajor ? [0, 3, 7] : isMinor ? [0, 3, 6] : baseIntervals;
    case "aug":
      return [0, 4, 8];
  }
}

function normalizeIntervals(intervals: number[]) {
  return [...new Set(intervals)].sort((a, b) => a - b);
}

function symbolForIntervals(intervals: number[]) {
  if (intervalsEqual(intervals, [0, 2, 7])) {
    return "sus2";
  }

  if (intervalsEqual(intervals, [0, 4, 7, 9])) {
    return "6";
  }

  if (intervals.includes(14) && !intervals.includes(10) && !intervals.includes(11)) {
    return `${triadSymbol(intervals)}add9`;
  }

  if (intervals.length >= 6) {
    return extensionSymbol(intervals, "13");
  }

  if (intervals.length >= 5) {
    return extensionSymbol(intervals, "9");
  }

  if (intervals.length >= 4) {
    return seventhSymbol(intervals);
  }

  return triadSymbol(intervals);
}

function extensionSymbol(intervals: number[], extension: "9" | "13") {
  const seventh = seventhSymbol(intervals);

  if (seventh === "maj7") {
    return `maj${extension}`;
  }

  if (seventh === "m7") {
    return `m${extension}`;
  }

  if (seventh === "7" || seventh === "") {
    return extension;
  }

  return `${seventh}${extension}`;
}

function seventhSymbol(intervals: number[]) {
  const triad = triadSymbol(intervals);
  const seventh = intervals[3];

  if (triad === "" && seventh === 11) {
    return "maj7";
  }

  if (triad === "" && seventh === 10) {
    return "7";
  }

  if (triad === "m" && seventh === 10) {
    return "m7";
  }

  if (triad === "m" && seventh === 11) {
    return "mMaj7";
  }

  if (triad === "dim" && seventh === 10) {
    return "m7b5";
  }

  if (triad === "dim" && seventh === 9) {
    return "dim7";
  }

  return triad;
}

function triadSymbol(intervals: number[]) {
  const triad = intervals.slice(0, 3).join(",");

  if (triad === "0,4,7") {
    return "";
  }

  if (triad === "0,3,7") {
    return "m";
  }

  if (triad === "0,3,6") {
    return "dim";
  }

  if (triad === "0,4,8") {
    return "aug";
  }

  if (triad === "0,3,8") {
    return "m#5";
  }

  if (triad === "0,5,7") {
    return "sus4";
  }

  return "";
}

function intervalsEqual(left: number[], right: number[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function normalizeSemitone(index: number) {
  return ((index % 12) + 12) % 12;
}
